import { esc } from "../ui/format.js";
import { uiCard, uiGet, uiMoney } from "../ui/kit.js";
import { emAct, emLetters, emLog, emMeter, emWireLetters } from "./empire.js";

/**
 * Two cards that sit on existing screens:
 *   Takeover defence (backend/takeover.py) on Public Markets: how exposed you
 *   are to a hostile bid, and the bid itself when one lands.
 *   Restructuring (backend/restructuring.py) on Banking: the creditors' terms
 *   when cash runs deep red, filing yourself, and the comeback tracker.
 */

async function renderTakeoverInto(host) {
  const t = await uiGet("/takeover");
  if (!t.is_public) { host.innerHTML = ""; return; }
  const v = t.vulnerability;
  host.innerHTML = `
    ${t.open.length ? uiCard("🦈 Someone wants to buy you", emLetters(t.open), { cls: "em-alarm" }) : ""}
    ${uiCard("Takeover defence", `${emMeter(v.risk, { label: "Takeover risk", lines: [{ at: 35, label: "Watched" }, { at: 70, label: "Circling" }] })}
      <ul class="tk-reasons">${v.reasons.map((r) => `<li>${esc(r)}</li>`).join("")}</ul>
      <p class="sx-card-sub">${t.bids} bid${t.bids === 1 ? "" : "s"} so far · ${t.defended} beaten off.</p>
      ${t.history.length ? emLog(t.history) : ""}`, { sub: t.rules })}`;
  emWireLetters(host);
}

async function renderRestructuringInto(host) {
  let r;
  try { r = await uiGet("/restructuring"); } catch (e) { host.innerHTML = ""; return; }
  const comeback = r.comeback;
  if (!r.open.length && !r.can_file && !comeback && !r.insolvency_streak) { host.innerHTML = ""; return; }
  const pct = comeback && comeback.peak_before > 0 ? Math.min(100, (100 * comeback.valuation_now) / comeback.peak_before) : 0;
  host.innerHTML = `
    ${r.open.length ? uiCard("⚖️ Your creditors want to talk", emLetters(r.open), { cls: "em-alarm" }) : ""}
    ${comeback ? uiCard(comeback.complete_month ? "🔥 Comeback complete" : "🔥 The comeback", `
      <div class="rs-come"><div class="rs-come-bar"><i style="width:${pct.toFixed(1)}%"></i></div>
      <p>${comeback.complete_month ? `At month ${comeback.complete_month} you passed your old peak of ${uiMoney(comeback.peak_before, { compact: true })}. Few founders ever do.`
        : `Valuation ${uiMoney(comeback.valuation_now, { compact: true })} of the ${uiMoney(comeback.peak_before, { compact: true })} you were worth before the fall (${pct.toFixed(0)}%).`}</p></div>`) : ""}
    ${!r.open.length && (r.can_file || r.insolvency_streak) ? uiCard("Restructuring", `
      <p>Cash ${uiMoney(r.terms.cash)} · debt ${uiMoney(r.terms.debt)} · ${r.insolvency_streak} month${r.insolvency_streak === 1 ? "" : "s"} deep in the red (creditors call at ${r.trigger}).</p>
      <p class="sx-card-sub">If you restructure: ${uiMoney(r.terms.written_off, { compact: true })} of debt becomes shares, ${uiMoney(r.terms.rescue_money, { compact: true })} of rescue money comes in, and you keep ${r.terms.founder_pct_after}% of the company (from ${r.terms.founder_pct_now}%).</p>
      ${r.can_file ? `<div class="em-actions"><button type="button" data-rs-file>Ask the creditors for terms now</button></div>` : ""}`, { sub: r.rules }) : ""}`;
  emWireLetters(host);
  const f = host.querySelector("[data-rs-file]");
  if (f) f.addEventListener("click", () => emAct("restructure_file"));
}

// Banking (views/banking.js) gets the restructuring card on top, without touching its own code.
const bankingBase = window.renderBanking;
if (bankingBase) {
  window.renderBanking = async (main, snap) => {
    await bankingBase(main, snap);
    const host = document.createElement("div");
    host.className = "em-inline";
    main.prepend(host);
    await renderRestructuringInto(host).catch(() => { host.remove(); });
  };
}

window.renderTakeoverInto = renderTakeoverInto;

export { renderRestructuringInto, renderTakeoverInto };
